import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { getAdminQustions } from "../../functions/getAdminQuestions";
import "./QuestionsContent.css";

const QUESTION_CSV_HEADERS = [
  "question_title",
  "question",
  "option_one",
  "option_two",
  "option_three",
  "option_one_impact_energy",
  "option_one_impact_time",
  "option_one_impact_health",
  "option_two_impact_energy",
  "option_two_impact_time",
  "option_two_impact_health",
  "option_three_impact_energy",
  "option_three_impact_time",
  "option_three_impact_health",
  "option_one_popup",
  "option_two_popup",
  "option_three_popup",
];

/* Max lengths in UI + CSV validation. If popups truncate after save, widen DB columns (see sql/extend_question_text_columns.sql). */
const TEXT_LIMITS = {
  question_title: 50,
  question: 250,
  option_one: 250,
  option_two: 250,
  option_three: 250,
  option_one_popup: 500,
  option_two_popup: 500,
  option_three_popup: 500,
};

const IMPACT_KEYS = [
  "option_one_impact_energy",
  "option_one_impact_time",
  "option_one_impact_health",
  "option_two_impact_energy",
  "option_two_impact_time",
  "option_two_impact_health",
  "option_three_impact_energy",
  "option_three_impact_time",
  "option_three_impact_health",
];

const MUSIC_MAX_BYTES = 10 * 1024 * 1024;

function parseCsvRow(line) {
  const cells = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQ = !inQ;
    } else if (c === "," && !inQ) {
      cells.push(cur.trim());
      cur = "";
    } else {
      cur += c;
    }
  }
  cells.push(cur.trim());
  return cells.map((s) => s.replace(/^"|"$/g, "").trim());
}

/**
 * Parse entire CSV: line breaks inside "double-quoted" fields do not start a new row (Excel / RFC 4180).
 * Required for files where question, options, or popups span multiple lines.
 */
function parseFullCsv(text) {
  const s = String(text).replace(/^\uFEFF/, "");
  if (!s.trim()) return { ok: false, error: "File is empty.", rows: [] };
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  let i = 0;

  while (i < s.length) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i += 2;
        } else {
          inQuotes = false;
          i++;
        }
      } else {
        field += c;
        i++;
      }
    } else if (c === '"') {
      inQuotes = true;
      i++;
    } else if (c === ",") {
      row.push(field.trim());
      field = "";
      i++;
    } else if (c === "\n") {
      row.push(field.trim());
      rows.push(row);
      row = [];
      field = "";
      i++;
    } else if (c === "\r") {
      if (s[i + 1] === "\n") {
        row.push(field.trim());
        rows.push(row);
        row = [];
        field = "";
        i += 2;
      } else {
        i++;
      }
    } else {
      field += c;
      i++;
    }
  }

  // Add the last row if there's content
  if (field.trim() || row.length > 0) {
    row.push(field.trim());
    rows.push(row);
  }

  if (inQuotes) {
    return {
      ok: false,
      error:
        'A text field has an opening " but no closing " before the end of the file. Close every quoted cell.',
      rows: [],
    };
  }

  const cleanRows = rows.filter((r) => r.length > 0 && r.some((cell) => String(cell ?? "").length > 0));
  return { ok: true, rows: cleanRows, error: "" };
}

function validateQuestionFields(raw, rowLabel = "Row") {
  const errors = [];
  const out = {};
  for (const key of Object.keys(TEXT_LIMITS)) {
    const v = raw[key] == null ? "" : String(raw[key]).trim();
    if (!v) errors.push(`${rowLabel}: ${key} cannot be empty`);
    else if (v.length > TEXT_LIMITS[key]) errors.push(`${rowLabel}: ${key} max ${TEXT_LIMITS[key]} characters`);
    out[key] = v;
  }
  for (const key of IMPACT_KEYS) {
    const v = raw[key] == null ? "" : String(raw[key]).trim();
    if (!v) errors.push(`${rowLabel}: ${key} cannot be empty`);
    else if (!/^-?\d+$/.test(v)) errors.push(`${rowLabel}: ${key} must be an integer`);
    else {
      const n = parseInt(v, 10);
      if (n < -100 || n > 100) errors.push(`${rowLabel}: ${key} must be between -100 and 100`);
      out[key] = String(n);
    }
  }
  return { errors, data: out };
}

const emptyQuestion = () => ({
  question_title: "",
  question: "",
  option_one: "",
  option_two: "",
  option_three: "",
  option_one_impact_energy: "",
  option_one_impact_time: "",
  option_one_impact_health: "",
  option_two_impact_energy: "",
  option_two_impact_time: "",
  option_two_impact_health: "",
  option_three_impact_energy: "",
  option_three_impact_time: "",
  option_three_impact_health: "",
  option_one_popup: "",
  option_two_popup: "",
  option_three_popup: "",
});

const escapeCsvCell = (val) => {
  const s = String(val ?? "").replace(/"/g, '""');
  return /[,"\n\r]/.test(s) ? `"${s}"` : s;
};

const rowToCsvLine = (row) =>
  QUESTION_CSV_HEADERS.map((h) => escapeCsvCell(row[h] ?? row[h.replace(/_([a-z])/g, (_, c) => c.toUpperCase())])).join(",");

const PREVIEW_TABLE_FIELDS = [
  "question_title",
  "question",
  "option_one",
  "option_two",
  "option_three",
  "music",
];

const PREVIEW_TABLE_HEADERS = {
  question_title: "Heading",
  question: "Question",
  option_one: "Option one",
  option_two: "Option two",
  option_three: "Option three",
  music: "Sound",
};

const QUESTIONS_PAGE_SIZE = 10;

function getQuestionMusicAudioSrc(row) {
  const urlField = row?.music_url ?? row?.musicUrl ?? row?.musicURL;
  if (urlField != null && String(urlField).trim()) {
    const u = String(urlField).trim();
    if (/^https?:\/\//i.test(u)) return u;
  }
  const name = String(row?.music ?? row?.music_file ?? "").trim();
  if (!name) return "";
  if (/^https?:\/\//i.test(name)) return name;
  const baseRaw = process.env.REACT_APP_S3_PATH || "";
  const base = baseRaw.replace(/\/?$/, "/");
  return base + name.replace(/^\//, "");
}

function QuestionMusicPlayer({ row }) {
  const src = getQuestionMusicAudioSrc(row);
  const onPlay = (e) => {
    document.querySelectorAll(".questions-table-audio").forEach((el) => {
      if (el !== e.target) el.pause();
    });
  };
  if (!src) return <span className="questions-music-empty">—</span>;
  return (
    <div className="questions-music-player">
      <audio className="questions-table-audio" controls preload="metadata" src={src} onPlay={onPlay}>
        <a href={src} target="_blank" rel="noopener noreferrer">
          Open audio
        </a>
      </audio>
    </div>
  );
}

export default function QuestionsContent() {
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme } = useSelector((state) => state.theme);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyQuestion());
  const [editId, setEditId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const csvInputRef = useRef(null);
  const musicInputRef = useRef(null);
  const [musicFile, setMusicFile] = useState(null);
  const [musicRemove, setMusicRemove] = useState(false);
  const [existingMusic, setExistingMusic] = useState("");

  const fetchQuestions = async () => {
    if (!adminToken || !currentTheme) {
      setLoading(false);
      setQuestions([]);
      return;
    }
    setLoading(true);
    try {
      const result = await getAdminQustions({ currentTheme, token: adminToken });
      const list = result?.allQuestions ?? result?.questions ?? [];
      setQuestions(Array.isArray(list) ? list : []);
    } catch {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [currentTheme, adminToken]);

  const totalPages = Math.max(1, Math.ceil(questions.length / QUESTIONS_PAGE_SIZE) || 1);
  const pageStartIndex = (currentPage - 1) * QUESTIONS_PAGE_SIZE;
  const pagedQuestions = questions.slice(pageStartIndex, pageStartIndex + QUESTIONS_PAGE_SIZE);

  useEffect(() => {
    if (questions.length === 0) {
      setCurrentPage(1);
      return;
    }
    const tp = Math.ceil(questions.length / QUESTIONS_PAGE_SIZE) || 1;
    setCurrentPage((p) => Math.min(Math.max(1, p), tp));
  }, [questions.length]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const openAddModal = (row = null) => {
    if (row) {
      setForm({
        question_title: row.question_title ?? row.questionTitle ?? "",
        question: row.question ?? "",
        option_one: row.option_one ?? "",
        option_two: row.option_two ?? "",
        option_three: row.option_three ?? "",
        option_one_impact_energy: row.option_one_impact_energy ?? "",
        option_one_impact_time: row.option_one_impact_time ?? "",
        option_one_impact_health: row.option_one_impact_health ?? "",
        option_two_impact_energy: row.option_two_impact_energy ?? "",
        option_two_impact_time: row.option_two_impact_time ?? "",
        option_two_impact_health: row.option_two_impact_health ?? "",
        option_three_impact_energy: row.option_three_impact_energy ?? "",
        option_three_impact_time: row.option_three_impact_time ?? "",
        option_three_impact_health: row.option_three_impact_health ?? "",
        option_one_popup: row.option_one_popup ?? "",
        option_two_popup: row.option_two_popup ?? "",
        option_three_popup: row.option_three_popup ?? "",
      });
      setEditId(row.id ?? row.question_id);
      setExistingMusic(String(row.music ?? row.music_file ?? "").trim());
    } else {
      setForm(emptyQuestion());
      setEditId(null);
      setExistingMusic("");
    }
    setMusicFile(null);
    setMusicRemove(false);
    if (musicInputRef.current) musicInputRef.current.value = "";
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setForm(emptyQuestion());
    setEditId(null);
    setMusicFile(null);
    setMusicRemove(false);
    setExistingMusic("");
    if (musicInputRef.current) musicInputRef.current.value = "";
  };

  const validateMusicFile = (file) => {
    if (!file) return null;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".mp3")) return "Music must be .mp3 only.";
    const allowed = ["audio/mpeg", "audio/mp3", "application/octet-stream"];
    if (file.type && !allowed.includes(file.type)) return "Music must be MP3 only.";
    if (file.size > MUSIC_MAX_BYTES) return "Music file must be 10 MB or less.";
    return null;
  };

  const setImpactField = (key, raw) => {
    const s0 = String(raw);
    let sign = "";
    let rest = s0;
    if (rest.startsWith("-")) {
      sign = "-";
      rest = rest.slice(1);
    }
    rest = rest.replace(/\D/g, "").slice(0, 3);
    if (sign && rest === "") {
      updateField(key, "-");
      return;
    }
    if (!sign && rest === "") {
      updateField(key, "");
      return;
    }
    let n = parseInt(sign + rest, 10);
    if (Number.isNaN(n)) {
      updateField(key, "");
      return;
    }
    if (n < -100) n = -100;
    if (n > 100) n = 100;
    updateField(key, String(n));
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Session or theme not set.", "error");
      return;
    }
    const { errors, data } = validateQuestionFields(form, "Form");
    if (errors.length) {
      Swal.fire({
        title: "Validation",
        html: errors.slice(0, 12).join("<br/>") + (errors.length > 12 ? "<br/>…" : ""),
        icon: "error",
      });
      return;
    }
    const musicErr = validateMusicFile(musicFile);
    if (musicErr) {
      Swal.fire({ title: "Music", text: musicErr, icon: "error" });
      return;
    }
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("currentTheme", currentTheme);
      if (organizationId) fd.append("organizationId", organizationId);
      if (sessionId) fd.append("sessionId", sessionId);
      Object.entries(data).forEach(([k, v]) => fd.append(k, v));
      if (editId) fd.append("id", String(editId));
      if (musicFile) fd.append("music", musicFile);
      if (editId && musicRemove && !musicFile) fd.append("music_remove", "true");
      if (editId && existingMusic && !musicFile && !musicRemove) fd.append("music_existing", existingMusic);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/addNewQuestion`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || json.error || "Failed to save question");
      Swal.fire("Success", editId ? "Question updated." : "Question added.", "success");
      closeAddModal();
      fetchQuestions();
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to save question.", "error");
    } finally {
      setAdding(false);
    }
  };

  const handleCsvSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      Swal.fire("Invalid file", "Please select a CSV file.", "error");
      e.target.value = "";
      return;
    }
    setCsvFile(file);
    e.target.value = "";
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      Swal.fire("Info", "Please choose a CSV file first.", "info");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Session or theme not set.", "error");
      return;
    }
    setUploading(true);
    try {
      const text = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || ""));
        r.onerror = () => reject(new Error("Could not read file"));
        r.readAsText(csvFile, "UTF-8");
      });
      const parsed = parseFullCsv(text);
      if (!parsed.ok) {
        Swal.fire({
          title: "CSV format",
          icon: "error",
          width: 640,
          html: `<div style="text-align:left">${parsed.error}<br/><br/><b>Tip:</b> In Excel, long text with line breaks must sit inside one cell. Save as <b>CSV UTF-8</b>. Each question = one full row (17 columns).</div>`,
        });
        setUploading(false);
        return;
      }
      const tableRows = parsed.rows;
      if (tableRows.length < 2) {
        Swal.fire("CSV", "Need a header row plus at least one question row.", "error");
        setUploading(false);
        return;
      }
      const headerCells = tableRows[0].map((c) => String(c ?? "").replace(/^\ufeff/, "").trim().toLowerCase());
      const expected = QUESTION_CSV_HEADERS.map((h) => h.toLowerCase());
      if (headerCells.length !== expected.length || !expected.every((h, idx) => headerCells[idx] === h)) {
        Swal.fire({
          title: "CSV",
          html: `Header must match exactly (music is not in CSV):<br/><code style="word-break:break-all;font-size:11px">${QUESTION_CSV_HEADERS.join(",")}</code>`,
          icon: "error",
        });
        setUploading(false);
        return;
      }
      const allErrors = [];
      const nCol = QUESTION_CSV_HEADERS.length;
      for (let i = 1; i < tableRows.length; i++) {
        const cells = tableRows[i];
        if (cells.length !== nCol) {
          allErrors.push(
            `<b>Question ${i}</b> (row ${i + 1} in sheet): need <b>${nCol}</b> columns, found <b>${cells.length}</b>. If you split one question across many lines without quotes, re-open in Excel and save again as CSV UTF-8.`
          );
          continue;
        }
        const row = {};
        QUESTION_CSV_HEADERS.forEach((h, j) => {
          row[h] = cells[j];
        });
        const { errors } = validateQuestionFields(row, `Question ${i}`);
        allErrors.push(...errors);
      }
      if (allErrors.length) {
        const show = allErrors.slice(0, 20).join("<br/>");
        Swal.fire({
          title: "CSV validation failed",
          html: show + (allErrors.length > 20 ? `<br/>… <i>+${allErrors.length - 20} more</i>` : ""),
          icon: "error",
          width: 680,
        });
        setUploading(false);
        return;
      }

      const questionsPayload = [];
      for (let i = 1; i < tableRows.length; i++) {
        const cells = tableRows[i];
        const row = {};
        QUESTION_CSV_HEADERS.forEach((h, j) => {
          row[h] = cells[j];
        });
        questionsPayload.push(row);
      }

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadCSV`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          currentTheme,
          questions: questionsPayload,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Upload failed");
      }
      Swal.fire("Success", "Questions CSV uploaded.", "success");
      setShowCsvModal(false);
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = "";
      fetchQuestions();
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to upload CSV.", "error");
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCsv = () => {
    const headerLine = QUESTION_CSV_HEADERS.join(",");
    const sampleRow = {
      question_title: "Sample title",
      question: "What is your choice?",
      option_one: "Option A",
      option_two: "Option B",
      option_three: "Option C",
      option_one_impact_energy: "10",
      option_one_impact_time: "-5",
      option_one_impact_health: "0",
      option_two_impact_energy: "0",
      option_two_impact_time: "0",
      option_two_impact_health: "5",
      option_three_impact_energy: "-5",
      option_three_impact_time: "10",
      option_three_impact_health: "0",
      option_one_popup: "Popup text for A",
      option_two_popup: "Popup text for B",
      option_three_popup: "Popup text for C",
    };
    const csv = [headerLine, rowToCsvLine(sampleRow)].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "health-sprint-questions-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Session or theme not set.", "error");
      return;
    }
    setExporting(true);
    try {
      const result = await getAdminQustions({ currentTheme, token: adminToken });
      const list = result?.allQuestions ?? result?.questions ?? [];
      if (!Array.isArray(list) || list.length === 0) {
        Swal.fire("Info", "No questions to export.", "info");
        return;
      }
      const headerLine = QUESTION_CSV_HEADERS.join(",");
      const rows = list.map((row) => rowToCsvLine(row));
      const csv = [headerLine, ...rows].join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "health-sprint-data-export.csv";
      a.click();
      URL.revokeObjectURL(url);
      // Swal.fire("Success", "CSV exported.", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to export.", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    });
    if (!isConfirmed) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/deleteQuestion`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          currentTheme,
          id,
          ...(organizationId ? { organizationId } : {}),
          ...(sessionId ? { sessionId } : {}),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.message || json.error || "Delete failed");
      }
      Swal.fire("Deleted!", "Question has been deleted.", "success");
      fetchQuestions();
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to delete question.", "error");
    }
  };

  const getPreviewCell = (row, key) => {
    const val = row[key] ?? row[key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())];
    const s = val == null ? "" : String(val);
    const max =
      key === "question_title"
        ? Math.min(40, TEXT_LIMITS.question_title)
        : key === "question"
          ? Math.min(80, TEXT_LIMITS.question)
          : String(key).includes("popup") || String(key).startsWith("option_")
            ? 100
            : 70;
    return s.length > max ? s.slice(0, max) + "…" : s || "—";
  };

  const rowId = (row) => row.id ?? row.question_id;

  return (
    <div className="questions-content questions-content--fullscreen">
      <header className="questions-content-header">
        <h1 className="questions-content-title">Questions</h1>
        <div className="questions-actions">
          <button type="button" className="questions-action-btn outline" onClick={() => openAddModal()}>
            <i className="fa fa-plus-square" /> Add Question
          </button>
          <button type="button" className="questions-action-btn outline" onClick={() => setShowCsvModal(true)}>
            <i className="fas fa-upload" /> Upload CSV
          </button>
          <button type="button" className="questions-action-btn outline" onClick={handleExportCsv} disabled={exporting}>
            <i className="fas fa-file-export" /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button type="button" className="questions-action-btn outline" onClick={downloadSampleCsv}>
            <i className="fa fa-download" /> Sample CSV
          </button>
        </div>
      </header>

      <div className="questions-card questions-content-table-card">
        <div className="questions-card-body">
          <div className="questions-table-wrap">
            {loading ? (
              <div className="questions-loading">Loading questions…</div>
            ) : (
              <div className="questions-table-container">
                <table className="questions-table questions-table--preview">
                  <thead>
                    <tr>
                      <th className="questions-table-col-no">No</th>
                      {PREVIEW_TABLE_FIELDS.map((key) => (
                        <th key={key} className={`questions-table-col-${key}`}>
                          {PREVIEW_TABLE_HEADERS[key] ?? key}
                        </th>
                      ))}
                      <th>Edit</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.length === 0 ? (
                      <tr>
                        <td colSpan={PREVIEW_TABLE_FIELDS.length + 3} className="questions-empty">
                          No questions yet. Add one or upload CSV.
                        </td>
                      </tr>
                    ) : (
                      pagedQuestions.map((row, idx) => (
                        <tr key={row.id ?? row.question_id ?? `${pageStartIndex}-${idx}`}>
                          <td className="questions-table-col-no">{pageStartIndex + idx + 1}</td>
                          {PREVIEW_TABLE_FIELDS.map((key) => (
                            <td
                              key={key}
                              className={`questions-table-cell questions-table-col-${key}${key === "music" ? " questions-table-cell--music" : ""}`}
                              title={key === "music" ? undefined : String(row[key] ?? row.music_file ?? "")}
                            >
                              {key === "music" ? <QuestionMusicPlayer row={row} /> : getPreviewCell(row, key)}
                            </td>
                          ))}
                          <td>
                            <button type="button" className="questions-btn-sm edit" onClick={() => openAddModal(row)}>
                              <i className="fas fa-edit" /> Edit
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="questions-btn-sm delete"
                              onClick={() => {
                                const id = rowId(row);
                                if (id != null) handleDelete(id);
                              }}
                              title={rowId(row) == null ? "No id" : ""}
                            >
                              <i className="fas fa-trash" /> Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {!loading && questions.length > QUESTIONS_PAGE_SIZE && (
                  <div className="questions-pagination" role="navigation" aria-label="Questions pages">
                    <button
                      type="button"
                      className="questions-pagination-btn"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                      <button
                        key={num}
                        type="button"
                        className={`questions-pagination-btn questions-pagination-num${num === currentPage ? " is-active" : ""}`}
                        onClick={() => setCurrentPage(num)}
                        aria-current={num === currentPage ? "page" : undefined}
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="questions-pagination-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </button>
                    <span className="questions-pagination-meta">
                      {pageStartIndex + 1}–{Math.min(pageStartIndex + QUESTIONS_PAGE_SIZE, questions.length)} of {questions.length}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showAddModal && (
        <>
          <div className="questions-modal-backdrop" aria-hidden="true" />
          <div className="questions-modal">
            <div className="questions-modal-header">
              <h4 className="questions-modal-title">{editId ? "Edit Question" : "Add Question"}</h4>
              <button type="button" className="questions-modal-close" onClick={closeAddModal}>
                &times;
              </button>
            </div>
            <div className="questions-modal-body">
              <form onSubmit={handleSingleSubmit} className="questions-form">
                <div className="questions-form-group full">
                  <label>Question title (max {TEXT_LIMITS.question_title} characters, required)</label>
                  <input
                    type="text"
                    value={form.question_title}
                    onChange={(e) => updateField("question_title", e.target.value.slice(0, TEXT_LIMITS.question_title))}
                    placeholder="Question title"
                    maxLength={TEXT_LIMITS.question_title}
                  />
                </div>
                <div className="questions-form-group full">
                  <label>Question (max {TEXT_LIMITS.question} characters, required)</label>
                  <textarea
                    value={form.question}
                    onChange={(e) => updateField("question", e.target.value.slice(0, TEXT_LIMITS.question))}
                    placeholder="Question text"
                    rows={3}
                    maxLength={TEXT_LIMITS.question}
                  />
                </div>
                {["one", "two", "three"].map((opt) => (
                  <React.Fragment key={opt}>
                    <div className="questions-form-group full">
                      <label>Option {opt} (max {TEXT_LIMITS[`option_${opt}`]} characters, required)</label>
                      <textarea
                        value={form[`option_${opt}`]}
                        onChange={(e) =>
                          updateField(`option_${opt}`, e.target.value.slice(0, TEXT_LIMITS[`option_${opt}`]))
                        }
                        placeholder={`Option ${opt}`}
                        rows={2}
                        maxLength={TEXT_LIMITS[`option_${opt}`]}
                      />
                    </div>
                    <div className="questions-form-group">
                      <label>Option {opt} impact — Energy / Time / Health (integer -100…100, required)</label>
                      <div className="questions-impact-row">
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="E"
                          value={form[`option_${opt}_impact_energy`]}
                          onChange={(e) => setImpactField(`option_${opt}_impact_energy`, e.target.value)}
                        />
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="T"
                          value={form[`option_${opt}_impact_time`]}
                          onChange={(e) => setImpactField(`option_${opt}_impact_time`, e.target.value)}
                        />
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="H"
                          value={form[`option_${opt}_impact_health`]}
                          onChange={(e) => setImpactField(`option_${opt}_impact_health`, e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="questions-form-group">
                      <label>Option {opt} popup (max {TEXT_LIMITS[`option_${opt}_popup`]} characters, required)</label>
                      <textarea
                        value={form[`option_${opt}_popup`]}
                        onChange={(e) =>
                          updateField(
                            `option_${opt}_popup`,
                            e.target.value.slice(0, TEXT_LIMITS[`option_${opt}_popup`])
                          )
                        }
                        placeholder={`Popup for option ${opt}`}
                        rows={2}
                        maxLength={TEXT_LIMITS[`option_${opt}_popup`]}
                      />
                    </div>
                  </React.Fragment>
                ))}
                <div className="questions-form-group full">
                  <label>Music (optional) — MP3 only, max 10 MB. Not used in CSV upload.</label>
                  <input
                    ref={musicInputRef}
                    type="file"
                    accept=".mp3,audio/mpeg,audio/mp3"
                    className="questions-file-hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      setMusicRemove(false);
                      if (f) {
                        if (f.size === 0) {
                          e.target.value = "";
                          setMusicFile(null);
                          return;
                        }
                        const err = validateMusicFile(f);
                        if (err) {
                          Swal.fire({ title: "Music", text: err, icon: "error" });
                          e.target.value = "";
                          setMusicFile(null);
                        } else setMusicFile(f);
                      } else setMusicFile(null);
                    }}
                  />
                  <div className="questions-csv-row">
                    <button type="button" className="questions-btn outline" onClick={() => musicInputRef.current?.click()}>
                      Choose music file
                    </button>
                    <span className="questions-filename">
                      {musicFile ? musicFile.name : existingMusic && !musicRemove ? `Current: ${existingMusic}` : "No file"}
                    </span>
                  </div>
                  {editId && existingMusic && (
                    <label className="questions-checkbox-label">
                      <input
                        type="checkbox"
                        checked={musicRemove}
                        onChange={(e) => {
                          setMusicRemove(e.target.checked);
                          if (e.target.checked) {
                            setMusicFile(null);
                            if (musicInputRef.current) musicInputRef.current.value = "";
                          }
                        }}
                      />
                      Remove current music
                    </label>
                  )}
                </div>
                <div className="questions-form-actions">
                  <button type="button" className="questions-btn outline" onClick={closeAddModal}>
                    Cancel
                  </button>
                  <button type="submit" className="questions-btn primary" disabled={adding}>
                    {adding ? "Saving…" : editId ? "Update" : "Add Question"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {showCsvModal && (
        <>
          <div className="questions-modal-backdrop" aria-hidden="true" />
          <div className="questions-modal questions-modal--sm">
            <div className="questions-modal-header">
              <h4 className="questions-modal-title">Upload CSV</h4>
              <button type="button" className="questions-modal-close" onClick={() => setShowCsvModal(false)}>
                &times;
              </button>
            </div>
            <div className="questions-modal-body">
              <input ref={csvInputRef} type="file" accept=".csv,text/csv" className="questions-file-hidden" onChange={handleCsvSelect} />
              <div className="questions-csv-row">
                <button type="button" className="questions-btn outline" onClick={() => csvInputRef.current?.click()}>
                  Choose file
                </button>
                <span className="questions-csv-filename">{csvFile?.name || "No file chosen"}</span>
              </div>
              <p className="questions-form-hint code">
                * <b>17 columns</b> per question row. Multi-line text must be in <b>one Excel cell</b> (quoted in CSV).<br />
                * Music is not in CSV — add via Add Question / Edit. Use <b>Sample CSV</b> + Save As CSV UTF-8 in Excel.<br />
                * Text limits: <b>question_title</b> <b>{TEXT_LIMITS.question_title}</b>; <b>question</b> <b>{TEXT_LIMITS.question}</b>; <b>option_one</b>–<b>option_three</b> <b>{TEXT_LIMITS.option_one}</b> each; popups <b>{TEXT_LIMITS.option_one_popup}</b> each.<br />
                * Validation: non-empty fields, text/impact limits. Server may replace existing questions for this theme.
              </p>
              {uploading && <div className="questions-progress">Uploading… please wait.</div>}
              <div className="questions-form-actions">
                <button type="button" className="questions-btn outline" onClick={() => setShowCsvModal(false)}>
                  Cancel
                </button>
                <button type="button" className="questions-btn primary" onClick={handleCsvUpload} disabled={!csvFile || uploading}>
                  {uploading ? "Uploading…" : "Upload"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}